// src/hooks/useAssistantChat.ts
//
// State machine for the global platform assistant (plan 2026-07-28, 3.1).
// Leaner sibling of ChatPage/useChatSession: same SSE streaming hook and the
// same shared message mapper, but no datasource pickers, routing banners, or
// charts/tables — the assistant never renders those. It streams text answers,
// multiple-choice clarifications, and ActionProposalCards.
//
// OWNERSHIP: called once by AssistantProvider, NOT by the drawer (plan
// 2026-08-02). The drawer receives the returned object as a prop, and the
// provider exposes `ask()` on the context so other surfaces — the dashboard
// chat bar today — can start a turn. Any second call site would create a second
// independent transcript competing for the same drawer.
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAgentStream } from "@/hooks/useAgentStream";
import {
  listAssistantConversations,
  loadAssistantMessages,
  persistMcSelection,
  type AssistantConversation,
} from "@/api/assistant";
import { mapBackendMessageToFrontend } from "@/utils/mapBackendMessage";
import { formatMessageDate } from "@/utils/formatMessageDate";
import { derivePageContext } from "@/utils/pageContext";
import { applyStreamError, type StreamErrorInfo } from "@/utils/streamError";
import type { MessageType } from "@/pages/ChatPage";

const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export type AssistantView = "chat" | "history";

/** The chat state machine, as handed to AssistantDrawer by AssistantProvider. */
export type AssistantChat = ReturnType<typeof useAssistantChat>;

export function useAssistantChat(opened: boolean) {
  const { user } = useAuth();
  const { activeWorkspaceId } = useWorkspace();

  const [conversations, setConversations] = useState<AssistantConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [view, setView] = useState<AssistantView>("chat");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Live progress label ("Checking your sources…") shown under the loader
  // while a turn has no committed text yet. Cleared when text arrives / stream
  // ends, so it never lingers.
  const [statusLabel, setStatusLabel] = useState<string | null>(null);

  // Streaming plumbing — mirrors ChatPage's text_delta/text_boundary handling.
  const streamingAssistantIdRef = useRef<string | null>(null);
  const committedLenRef = useRef(0);
  const streamedLenRef = useRef(0);
  const lastUserMessageRef = useRef<string | null>(null);

  // Always reflects the live active conversation id. Written directly by
  // newConversation/selectConversation as well, so it is authoritative at CALL
  // time — assigning it only here (during render) would let a
  // `newConversation(); send(x)` pair in one handler send with the previous
  // conversation's id.
  const activeConversationIdRef = useRef<string | null>(null);
  activeConversationIdRef.current = activeConversationId;

  // Monotonic turn identity. Bumped whenever the active conversation changes
  // (new/selected). `send` captures it before its await and re-checks after, to
  // decide whether the reply it is holding still belongs on screen.
  //
  // This deliberately replaces an earlier `activeConversationId` comparison:
  // two UNSAVED conversations are both `null`, so id equality could not tell an
  // aborted turn from the live one. The aborted turn then adopted the live
  // turn's slot and the real answer was silently dropped.
  const turnEpochRef = useRef(0);

  // Mirrors the stream's loading state for synchronous reads (ask() guards on
  // it before starting a turn; state would be a render behind).
  const isStreamingRef = useRef(false);

  const updateMessage = useCallback(
    (id: string, updater: (m: MessageType) => MessageType) => {
      setMessages((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
    },
    [],
  );

  const {
    isLoading: streamLoading,
    interim,
    sendMessage: streamSendMessage,
    abort,
  } = useAgentStream({
    onInit: (data) => {
      if (data.assistantMessageId) {
        streamingAssistantIdRef.current = data.assistantMessageId;
        committedLenRef.current = 0;
        streamedLenRef.current = 0;
        setMessages((prev) => [
          ...prev,
          {
            id: data.assistantMessageId!,
            sender: "ai",
            content: "",
            timestamp: formatMessageDate(new Date()),
          },
        ]);
      }
    },
    // The `status` event's `step` is the friendly label ("Checking your
    // sources…", from _tool_step_label). NOT step_complete — that event's
    // `step` is the raw tool key (the chat route sets step=tool_name), which
    // would show "list_managed_sources" verbatim. The status label already
    // stays visible while the tool runs, so status alone is enough.
    onStatus: (s) => setStatusLabel(s.step || null),
    onTextDelta: (content) => {
      const id = streamingAssistantIdRef.current;
      if (!id) return;
      // Real answer text is arriving — drop the progress label (idempotent:
      // React bails out when it's already null, so no stale-closure concern).
      setStatusLabel(null);
      streamedLenRef.current += content.length;
      updateMessage(id, (m) => ({ ...m, content: m.content + content }));
    },
    onTextBoundary: (action) => {
      if (action === "commit") {
        committedLenRef.current = streamedLenRef.current;
        return;
      }
      // "discard" — strip the provisional tail streamed before a real tool call.
      const id = streamingAssistantIdRef.current;
      if (!id) return;
      const keep = committedLenRef.current;
      updateMessage(id, (m) => ({ ...m, content: m.content.slice(0, keep) }));
      streamedLenRef.current = keep;
    },
  });

  // Load the conversation list the first time the drawer is opened (and refresh
  // on account switch, which changes the user identity).
  const accountId = user?.activeAccount?.id ?? null;
  const hasLoadedListRef = useRef(false);
  const refreshConversations = useCallback(async () => {
    try {
      const rows = await listAssistantConversations();
      setConversations(rows);
    } catch {
      // Non-critical — the list stays as-is; the composer still works.
    }
  }, []);

  // Account switch → drop EVERYTHING account-scoped, not just the list. A real
  // switch currently unmounts this provider anyway (switchAccount sets
  // `loading`, and AppRoutes renders the page loader instead of <Routes>), but
  // that is an implicit property of an unrelated component. Paths that mutate
  // `user` without the loading flag — a proactive token refresh picking up an
  // account switched in another tab — do not unmount, and post-lift this hook
  // lives in a provider that survives navigation. Clearing here makes the
  // guarantee local and explicit.
  useEffect(() => {
    hasLoadedListRef.current = false;
    abort();
    turnEpochRef.current += 1;
    streamingAssistantIdRef.current = null;
    activeConversationIdRef.current = null;
    setConversations([]);
    setMessages([]);
    setActiveConversationId(null);
    setLoadError(null);
    setStatusLabel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- account switch only
  }, [accountId]);

  useEffect(() => {
    if (!opened || hasLoadedListRef.current || !accountId) return;
    hasLoadedListRef.current = true;
    void refreshConversations();
  }, [opened, accountId, refreshConversations]);

  const newConversation = useCallback(() => {
    abort();
    turnEpochRef.current += 1;
    streamingAssistantIdRef.current = null;
    activeConversationIdRef.current = null;
    setActiveConversationId(null);
    setMessages([]);
    setLoadError(null);
    setView("chat");
  }, [abort]);

  const selectConversation = useCallback(
    async (id: string) => {
      abort();
      turnEpochRef.current += 1;
      streamingAssistantIdRef.current = null;
      activeConversationIdRef.current = id;
      setActiveConversationId(id);
      setView("chat");
      setLoadError(null);
      setIsLoadingMessages(true);
      try {
        const rows = await loadAssistantMessages(id, 50);
        setMessages(rows.map(mapBackendMessageToFrontend));
      } catch {
        setLoadError("This conversation could not be loaded. Please try again.");
        setMessages([]);
      } finally {
        setIsLoadingMessages(false);
      }
    },
    [abort],
  );

  const send = useCallback(
    async (input: string, opts?: { skipUserMessage?: boolean }) => {
      const trimmed = input.trim();
      if (!trimmed || !user) return;
      lastUserMessageRef.current = trimmed;
      setStatusLabel(null); // fresh turn — no stale label from the previous one

      const acct = user.activeAccount?.id;
      const workspaceId = activeWorkspaceId || user.defaultWorkspaceId;
      if (!acct || !workspaceId) {
        setLoadError("Your session is missing an account or workspace. Please reload.");
        return;
      }

      if (!opts?.skipUserMessage) {
        setMessages((prev) => [
          ...prev,
          {
            id: newId(),
            sender: "user",
            content: trimmed,
            firstName: user.firstName ?? undefined,
            timestamp: formatMessageDate(new Date()),
          },
        ]);
      }

      const conversationIdToSend = activeConversationIdRef.current;
      const turnEpoch = turnEpochRef.current;

      // Derive page context at SEND time (not drawer-open time) so it reflects
      // wherever the user navigated to before asking. Structured fields only;
      // the backend re-validates and resolves entity_id account-scoped.
      const pageContext = derivePageContext(window.location.pathname);

      const result = await streamSendMessage({
        account_id: acct,
        workspace_id: workspaceId,
        user_id: user.id,
        message: trimmed,
        conversation_id: conversationIdToSend,
        surface: "assistant",
        ...(pageContext ? { page_context: pageContext } : {}),
      });

      // Turn-identity check FIRST, before touching any shared ref. If the
      // conversation changed while this stream was in flight — the user picked
      // another one, hit "+", or ask() started a fresh turn — this reply
      // belongs to the previous turn. It is persisted server-side, so: don't
      // mutate the now-visible transcript, don't hijack the active conversation
      // id, and above all don't clear streamingAssistantIdRef, which the LIVE
      // turn is using to route its text deltas.
      //
      // A user-initiated abort lands here too: useAgentStream returns the
      // partial result silently (with conversation_id already set from `init`)
      // rather than throwing, so an aborted turn reaches this point looking
      // exactly like a completed one.
      if (turnEpochRef.current !== turnEpoch) {
        return;
      }

      const hadStreamingPlaceholder = streamingAssistantIdRef.current !== null;
      streamingAssistantIdRef.current = null;

      // New conversation → adopt the server id and surface it in the list.
      if (result.conversationId && !conversationIdToSend) {
        setActiveConversationId(result.conversationId);
        void refreshConversations();
      }

      const upsertAssistantMessage = (aiMessage: MessageType) => {
        if (hadStreamingPlaceholder && result.assistantMessageId) {
          updateMessage(result.assistantMessageId, (prev) => ({
            ...prev,
            ...aiMessage,
            // Keep the streamed text if the final text matches (no flash).
            content: aiMessage.content || prev.content,
          }));
        } else {
          setMessages((prev) => [...prev, aiMessage]);
        }
      };

      const finalResult = result.response;

      const streamError: StreamErrorInfo | null = result.error
        ? {
            code: result.errorCode || "STREAM_ERROR",
            message: result.error,
            recoverable: result.errorRecoverable,
          }
        : null;

      if (streamError && !finalResult) {
        // Committed text already on screen survives the error — it is shown
        // with the error as a notice underneath (incident 2026-08-07). Only a
        // turn with nothing worth keeping becomes the error bubble.
        const committedLen = committedLenRef.current;
        if (hadStreamingPlaceholder && result.assistantMessageId) {
          updateMessage(result.assistantMessageId, (prev) =>
            applyStreamError(prev, committedLen, streamError, formatMessageDate(new Date())),
          );
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: result.assistantMessageId || newId(),
              sender: "ai",
              responseType: "error",
              content: streamError.message,
              error: {
                code: streamError.code,
                message: streamError.message,
                recoverable: streamError.recoverable,
              },
              timestamp: formatMessageDate(new Date()),
            },
          ]);
        }
        return;
      }

      if (finalResult) {
        const isError = finalResult.response_type === "error";
        const isClarification = finalResult.response_type === "clarification-needed";
        let text: string;
        if (isError) {
          text = finalResult.error?.message || finalResult.response_description || "Something went wrong.";
        } else if (isClarification) {
          // The tool-use pipeline puts the committed explanation (or, failing
          // that, the mc question) in response_description — `clarification`
          // is a legacy LangGraph shape this pipeline never fills.
          text =
            finalResult.response_description ||
            finalResult.multiple_choice?.question ||
            "Could you clarify that?";
        } else {
          text = finalResult.response_description || "";
        }

        upsertAssistantMessage({
          id: result.assistantMessageId || newId(),
          sender: "ai",
          responseType: (finalResult.response_type === "partial"
            ? "answer"
            : finalResult.response_type) as MessageType["responseType"],
          content: text,
          error: isError ? finalResult.error : undefined,
          // Errored-but-still-assembled turn: keep the answer, note the error.
          streamError: streamError && !isError ? streamError : undefined,
          multipleChoice: finalResult.multiple_choice ?? undefined,
          actionProposals: result.actionProposals ?? undefined,
          timestamp: formatMessageDate(new Date()),
        });
      }
    },
    [user, activeWorkspaceId, streamSendMessage, refreshConversations, updateMessage],
  );

  const selectMultipleChoice = useCallback(
    (messageId: string, option: string) => {
      // Persist the selection (fire-and-forget, like the insights chat), then
      // send the option as the next user turn without a duplicate user bubble.
      void persistMcSelection(messageId, option).catch(() => {});
      void send(option, { skipUserMessage: true });
    },
    [send],
  );

  const retry = useCallback(() => {
    if (lastUserMessageRef.current) void send(lastUserMessageRef.current);
  }, [send]);

  isStreamingRef.current = streamLoading;

  return {
    // state
    conversations,
    activeConversationId,
    messages,
    view,
    isLoadingMessages,
    loadError,
    isStreaming: streamLoading,
    /** Synchronous mirror of `isStreaming` — for guards that run before render. */
    isStreamingRef,
    statusLabel,
    interim,
    // actions
    setView,
    newConversation,
    selectConversation,
    send,
    selectMultipleChoice,
    retry,
    abort,
  };
}
